import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, X, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useSearch, getTypeLabel, SearchResult } from "@/hooks/useSearch";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const QuickResult = ({ result, onClick }: { result: SearchResult; onClick: () => void }) => {
  const isHashLink = result.url.includes("#");
  
  const content = (
    <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors cursor-pointer group">
      {result.image ? (
        <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
          <img 
            src={result.image} 
            alt={result.title}
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="w-12 h-12 rounded-md bg-cream flex items-center justify-center flex-shrink-0">
          <Search className="w-5 h-5 text-muted-foreground" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-sans text-sm text-foreground truncate group-hover:text-primary transition-colors">
          {result.title}
        </p>
        <p className="text-xs text-muted-foreground">
          {getTypeLabel(result.type)}
        </p>
      </div>
      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
    </div>
  );

  if (isHashLink) {
    return (
      <a href={result.url} onClick={onClick}>
        {content}
      </a>
    );
  }

  return (
    <Link to={result.url} onClick={onClick}>
      {content}
    </Link>
  );
};

const SearchDialog = ({ open, onOpenChange }: SearchDialogProps) => {
  const [query, setQuery] = useState("");
  const results = useSearch(query);
  const navigate = useNavigate();
  const displayResults = results.slice(0, 5);

  useEffect(() => {
    if (!open) {
      // Reset query when dialog closes
      setTimeout(() => setQuery(""), 200);
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      onOpenChange(false);
      navigate(`/sok?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleResultClick = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden" aria-describedby={undefined}>
        <VisuallyHidden>
          <DialogTitle>Søk</DialogTitle>
        </VisuallyHidden>
        
        <form onSubmit={handleSubmit}>
          {/* Search Input */}
          <div className="relative border-b border-border">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Søk etter bunader, tilbehør, spørsmål..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-12 pr-12 py-6 text-base border-0 focus-visible:ring-0 rounded-none"
              autoFocus
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </form>

        {/* Quick Results */}
        {query.trim() && (
          <div className="p-3 max-h-[50vh] overflow-y-auto">
            {displayResults.length > 0 ? (
              <>
                <div className="space-y-1">
                  {displayResults.map((result) => (
                    <QuickResult 
                      key={`${result.type}-${result.id}`} 
                      result={result} 
                      onClick={handleResultClick}
                    />
                  ))}
                </div>
                
                {results.length > 5 && (
                  <button
                    onClick={() => {
                      onOpenChange(false);
                      navigate(`/sok?q=${encodeURIComponent(query.trim())}`);
                    }}
                    className="w-full mt-3 py-3 text-center text-sm font-sans text-primary hover:text-primary/80 transition-colors border-t border-border"
                  >
                    Se alle {results.length} resultater
                  </button>
                )}
              </>
            ) : (
              <div className="py-8 text-center">
                <p className="text-muted-foreground font-sans text-sm mb-2">
                  Ingen treff for "{query}"
                </p>
                <p className="text-muted-foreground/60 font-sans text-xs">
                  Prøv et annet søkeord
                </p>
              </div>
            )}
          </div>
        )}

        {/* Popular Searches (when empty) */}
        {!query.trim() && (
          <div className="p-4">
            <p className="text-xs font-sans text-muted-foreground uppercase tracking-wider mb-3">
              Populære søk
            </p>
            <div className="flex flex-wrap gap-2">
              {["Grafferdrakt", "Lundebybunad", "Sko", "Prøvetime", "Tilbehør"].map((term) => (
                <button
                  key={term}
                  onClick={() => setQuery(term)}
                  className="px-3 py-1.5 bg-cream text-foreground rounded-full text-sm font-sans hover:bg-muted transition-colors"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
